import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { TS_EditableItemComponent } from '@nu-art/thunderstorm-frontend/components/TS_EditableItemComponent/TS_EditableItemComponent';
import './editors.scss';
import { Button, LL_H_C, LL_V_L } from '@nu-art/thunderstorm-frontend/index';
import { Editor_Case } from './Editor_Case.js';
import { deepClone, exists, filterDuplicates, filterInstances } from '@nu-art/ts-common';
import { DropDown_ExpressionLeftHand } from '../../../shared-components/app-dropdowns.js';
import { validateLeftHandBaseExists } from './utils.js';
import { QRangeInputV3 } from '../../../../../q-components/index.js';
import { Editor_TimeRange } from '../../../../../healthcare-space/index.js';
import { ModuleFE_Expression, ModuleFE_Value, ModuleFE_Variable, } from '../../../../../../_entity.js';
import { AllDeltaLogic, getBaseDeltaOperator, getLeftHandType, LogicType_Exists, LogicType_IsChiefComplaint, LogicType_NotEquals, LogicType_NotExists, LogicType_NotInSet, LogicType_Self, LogicTypes_ToFiler, NormalRangeOperators, NormalRangeVarLogic, SelfOperators, valueType_Range, valueType_StandardRange } from '@app/hcs-shared';
import { DropDown_SampleIndex } from '../../../../../../_entity/expression/ui-components.js';
export class Editor_Atom extends TS_EditableItemComponent {
    deriveStateFromProps(nextProps, state) {
        state = super.deriveStateFromProps(nextProps, state);
        const cases = state.editable.get('cases');
        const fallbackValue = state.editable.get('fallbackValue');
        const sampleInfo = state.editable.get('sampleInfo');
        const deltaTimeDiff = state.editable.get('deltaTimeDiff');
        state.toggleCasesValue = (exists(cases?.[0].value) || exists(fallbackValue)) ?? false;
        state.toggleSampleRestrictions = exists(sampleInfo) ?? false;
        state.toggleExpiry = exists(sampleInfo?.expiry) ?? false;
        state.toggleDeltaTimeDiff = exists(deltaTimeDiff) ?? false;
        state.viewMode = nextProps.viewMode;
        state.mappers ??= {
            variableMap: ModuleFE_Variable.cache.arrayToMap((_var) => _var._id),
            valueMap: ModuleFE_Value.cache.arrayToMap((_var) => _var._id),
            expressionMap: ModuleFE_Expression.cache.arrayToMap((_var) => _var._id),
        };
        return state;
    }
    //######################### Logic #########################
    addEmptyCase = async () => {
        const atomItem = this.state.editable.item;
        const cases = atomItem.cases?.map((_case) => {
            if (!exists(_case.value))
                _case.value = [];
            return _case;
        });
        const deltaCaseOperator = getBaseDeltaOperator(cases?.[0].caseOperator);
        const newCases = [...cases, { caseOperator: deltaCaseOperator, value: [] }];
        await this.state.editable.updateObj({
            cases: newCases,
            fallbackValue: [0, 0]
        });
    };
    toggleAddValue = () => {
        this.setState({ toggleCasesValue: !this.state.toggleCasesValue }, async () => {
            const changes = {
                cases: deepClone(this.state.editable.get('cases')) ?? [],
                fallbackValue: [0, 0]
            };
            changes.cases?.forEach((_case) => _case.value = []);
            if (!this.state.toggleCasesValue) {
                // Remove the value from the case object
                if (changes.cases)
                    delete changes.cases[0].value;
                changes.fallbackValue = undefined;
            }
            await this.state.editable.updateObj(changes);
        });
    };
    toggleAddSample = () => {
        this.setState({ toggleSampleRestrictions: !this.state.toggleSampleRestrictions }, async () => {
            //set default value for sample object to make sure ui renders correctly
            const changes = {
                sampleInfo: { sampleIndex: 'latest' }
            };
            //If click removes the value just make it undefined to delete it
            if (!this.state.toggleSampleRestrictions)
                changes.sampleInfo = undefined;
            //Update the editable instance
            await this.state.editable.updateObj(changes);
        });
    };
    toggleAddExpiry = () => {
        this.setState({ toggleExpiry: !this.state.toggleExpiry }, async () => {
            const sampleInfo = this.state.editable.get('sampleInfo');
            //set default value for expiry to make sure ui renders correctly
            const changes = {
                sampleInfo: {
                    sampleIndex: sampleInfo.sampleIndex,
                    timeConstraint: sampleInfo.timeConstraint,
                    expiry: [undefined, undefined]
                }
            };
            //If click removes the value just make it undefined to delete it
            if (!this.state.toggleExpiry) {
                if (!changes.sampleInfo?.timeConstraint)
                    delete changes.sampleInfo?.timeConstraint;
                delete changes.sampleInfo?.expiry;
            }
            //Update the editable instance
            await this.state.editable.updateObj(changes);
        });
    };
    toggleDeltaTime = () => {
        this.setState({ toggleDeltaTimeDiff: !this.state.toggleDeltaTimeDiff }, async () => {
            if (!this.state.toggleDeltaTimeDiff)
                return this.state.editable.update('deltaTimeDiff', undefined);
            await this.state.editable.update('deltaTimeDiff', [undefined, undefined]);
        });
    };
    /**
     * this function will be passed as a prop to the case in order to be triggered on delete
     * @param index the index of the deleted case in the cases array.
     */
    deleteCase = async (index) => {
        const atomItem = this.state.editable;
        const changes = {};
        changes.cases = deepClone(atomItem.get('cases')) ?? [];
        changes.cases?.splice(index, 1);
        // If the atom has 0 cases delete it from the term and break the execution
        if (!changes.cases?.length)
            return this.props.deleteAtom();
        await this.state.editable.updateObj(changes);
        this.forceUpdate();
    };
    /**
     * Numeric, Range and self operators needs to be filtered upon creation of multiple cases to avoid contradictions
     */
    getOperatorsToFilter = () => {
        const cases = this.state.editable.get('cases');
        const usedOperators = filterInstances(cases.map(_case => _case.caseOperator));
        const operatorsToFilter = [];
        if (cases.length > 1) {
            //Atoms with more than one case can't use self, exists, or isChiefComplaint
            operatorsToFilter.push(LogicType_Self, LogicType_Exists, LogicType_NotExists, LogicType_IsChiefComplaint, LogicType_NotEquals);
            const valueType = getLeftHandType(this.state.editable.get('leftHand'), this.state.mappers);
            if (valueType === 'standard-range' && cases[0].caseOperator) {
                if (NormalRangeOperators.includes(cases[0].caseOperator)) {
                    operatorsToFilter.push(...NormalRangeVarLogic.filter(logic => !NormalRangeOperators.includes(logic)));
                }
                else
                    operatorsToFilter.push(...NormalRangeVarLogic.filter(logic => NormalRangeOperators.includes(logic)));
            }
        }
        usedOperators.forEach(operator => {
            if (LogicTypes_ToFiler.includes(operator))
                operatorsToFilter.push(operator);
        });
        return filterDuplicates(operatorsToFilter);
    };
    shouldAllowSampleConstrains = () => {
        const leftHand = this.state.editable.get('leftHand');
        if (!leftHand)
            return false;
        const firstCase = this.state.editable.get('cases')[0];
        const acceptableValueTypes = [valueType_StandardRange, valueType_Range];
        return acceptableValueTypes.includes(getLeftHandType(leftHand, this.state.mappers)) && !AllDeltaLogic.includes(firstCase?.caseOperator);
    };
    //######################### Render #########################
    renderButtons = () => {
        if (this.state.viewMode || exists(this.props.atoms))
            return '';
        const editableAtom = this.state.editable;
        const addValueLabel = this.state.toggleCasesValue ? 'Remove Value' : 'Add Value';
        const addSampleLabel = this.state.toggleSampleRestrictions ? 'Remove Sample Constrains' : 'Add Sample Constrains';
        const addExpiryLabel = this.state.toggleExpiry ? 'Remove Expiry' : 'Add Expiry';
        const addDeltaTimeLabel = this.state.toggleDeltaTimeDiff ? 'Remove Delta Time Constrain' : 'Add Delta Time Constrain';
        const cases = editableAtom.get('cases');
        const isSingleCaseAtom = SelfOperators.includes(cases?.[0].caseOperator) || cases?.[0].caseOperator === LogicType_Exists || cases?.[0].caseOperator === LogicType_NotExists || cases?.[0].caseOperator === LogicType_IsChiefComplaint;
        const disabled = !editableAtom.get('leftHand') || isSingleCaseAtom || !validateLeftHandBaseExists(editableAtom.get('leftHand'));
        // @ts-ignore
        const isAddCaseDisabled = disabled || [LogicType_NotEquals, LogicType_NotInSet].includes(cases?.[0].caseOperator);
        const shouldRenderDeltaTimeDiff = AllDeltaLogic.includes(cases?.[0].caseOperator) || this.state.toggleDeltaTimeDiff;
        const shouldRenderSampleConstrain = this.shouldAllowSampleConstrains();
        const sampleInfo = this.state.editable.get('sampleInfo');
        return _jsxs(LL_H_C, { style: { gap: 5 }, children: [_jsx(Button, { variant: 'primary', disabled: isAddCaseDisabled, onClick: this.addEmptyCase, className: 'add-entity-btn', children: "Add Case" }), (cases?.length ?? 0) <= 1 &&
                    _jsx(Button, { variant: 'secondary', disabled: disabled, onClick: this.toggleAddValue, className: 'add-entity-btn', children: addValueLabel }), shouldRenderSampleConstrain &&
                    _jsx(Button, { variant: 'secondary', onClick: this.toggleAddSample, className: 'add-entity-btn', children: addSampleLabel }), exists(sampleInfo) &&
                    _jsx(Button, { variant: 'secondary', onClick: this.toggleAddExpiry, className: 'add-entity-btn', children: addExpiryLabel }), shouldRenderDeltaTimeDiff &&
                    _jsx(Button, { variant: 'secondary', onClick: this.toggleDeltaTime, className: 'add-entity-btn', children: addDeltaTimeLabel })] });
    };
    renderAddElseValue = () => {
        if (!this.state.toggleCasesValue)
            return '';
        return _jsx(_Fragment, { children: _jsxs(LL_H_C, { className: 'match_width condition-container else-value', children: [_jsx("div", { className: 'label', children: "Else:" }), _jsxs(LL_H_C, { className: 'condition-value', children: [_jsx("div", { className: 'divider' }), _jsx(QRangeInputV3, { editable: this.state.editable, prop: 'fallbackValue', className: 'term-value-input', placeholder: 'Value', shouldReset: true, showErrorTooltip: true })] })] }) });
    };
    renderSampleConstrains = () => {
        if (!this.state.toggleSampleRestrictions)
            return '';
        const editableSample = this.state.editable.editProp('sampleInfo', {});
        const editableTimeRange = editableSample.editProp('timeConstraint', []);
        return _jsxs(LL_H_C, { className: 'match_width condition-container else-value', children: [_jsxs(LL_H_C, { className: 'label-container', children: [_jsx("div", { className: 'label', children: "Sample Constrains:" }), _jsx(DropDown_SampleIndex.editable, { canUnselect: true, showErrorTooltip: true, editable: editableSample, onSelected: async (value) => {
                                if (value)
                                    return editableSample.updateObj({ sampleIndex: value });
                                await editableSample.updateObj({ sampleIndex: undefined });
                            }, prop: 'sampleIndex' })] }), _jsxs(LL_H_C, { className: 'condition-value sample-constrains', children: [_jsx("div", { className: 'divider' }), _jsx(Editor_TimeRange, { editable: editableTimeRange })] })] });
    };
    renderExpiry = () => {
        if (!this.state.toggleExpiry)
            return '';
        const editableSample = this.state.editable.editProp('sampleInfo', {});
        const editableExpiry = editableSample.editProp('expiry', []);
        return _jsxs(LL_H_C, { className: 'match_width condition-container else-value', children: [_jsx(LL_H_C, { className: 'label-container', children: _jsx("div", { className: 'label', children: "Expiry:" }) }), _jsxs(LL_H_C, { className: 'condition-value expiry-container', children: [_jsx("div", { className: 'divider' }), _jsx(Editor_TimeRange, { editable: editableExpiry })] })] });
    };
    renderDeltaTimeDiff = () => {
        if (!this.state.toggleDeltaTimeDiff)
            return '';
        const editableDeltaTimeDiff = this.state.editable.editProp('deltaTimeDiff', []);
        return _jsxs(LL_H_C, { className: 'match_width condition-container else-value', children: [_jsx(LL_H_C, { className: 'label-container', children: _jsx("div", { className: 'label', children: "Time Constraint:" }) }), _jsxs(LL_H_C, { className: 'condition-value expiry-container', children: [_jsx("div", { className: 'divider' }), _jsx(Editor_TimeRange, { editable: editableDeltaTimeDiff })] })] });
    };
    renderDeltaInfoContainer = () => {
        if (!this.state.toggleExpiry && !this.state.toggleSampleRestrictions)
            return '';
        return _jsxs(LL_V_L, { className: 'atom-editor__delta-info', children: [this.renderSampleConstrains(), this.renderExpiry()] });
    };
    renderLeftHandDropdown = (index) => {
        const editableAtom = this.state.editable;
        return _jsx(DropDown_ExpressionLeftHand, { index: index, atoms: this.props.atoms, queryFilter: item => !this.props.usedVarIds.includes(item._id), editable: editableAtom, disabled: editableAtom.get('cases').length > 1 || this.props.viewMode, selected: editableAtom.get('leftHand') });
    };
    renderCases = () => {
        const atomItem = this.state.editable.item;
        const editableCases = this.state.editable.editProp('cases', []);
        const withValue = this.state.toggleCasesValue || (atomItem.cases?.length ?? 0) > 1;
        const leftHand = this.state.editable.get('leftHand');
        const atom = this.props.atoms?.find((atom, index) => leftHand?.id === String(index + 1));
        return _jsx(_Fragment, { children: atomItem.cases?.map((_case, index) => {
                const editableCase = editableCases.editProp(index, {});
                return _jsxs(LL_H_C, { style: { alignItems: 'flex-start', gap: '10px' }, className: "match_width flex__grow flex__space-between", children: [this.renderLeftHandDropdown(index), _jsx(Editor_Case, { leftHand: this.state.editable.get('leftHand'), deleteCase: () => this.deleteCase(index), operatorsToFilter: this.getOperatorsToFilter(), editable: editableCase, atom: atom, viewMode: this.props.viewMode, disableOperatorSelection: this.state.editable.get('cases').length > 1, withValue: withValue })] }, `case-${index}`);
            }) });
    };
    render() {
        return _jsxs(_Fragment, { children: [this.renderCases(), this.renderAddElseValue(), this.renderDeltaInfoContainer(), this.renderDeltaTimeDiff(), this.renderButtons()] });
    }
}
//# sourceMappingURL=Editor_Atom.js.map